import { useMutation, useQueries } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import {
  getTailoredHighlightsStatusOptions,
  postTailoredHighlightsMutation,
} from 'client/@tanstack/react-query.gen';
import type { TailoredHighlightDraft } from 'client/types.gen';
import { IconAlertTriangle } from 'components/UI/Icons/AlertTriangle';
import { IconLoader } from 'components/UI/Icons/Loader';
import { IconRefreshCw } from 'components/UI/Icons/RefreshCw';
import { IconSparkles } from 'components/UI/Icons/Sparkles';
import { Button } from 'components/UI/Tailwind/Button';
import { FormCheckbox } from 'components/UI/Tailwind/FormCheckbox';
import { FormTextArea } from 'components/UI/Tailwind/FormTextArea';
import type { Schema as ParentSchema } from './Step2';

/**
 * AI-drafted highlights written for this pipeline, one candidate at a time.
 *
 * Drafting follows the auto-tag preview: POST queues a draft, the status is
 * polled, and nothing is stored on the server until the advisor saves or
 * publishes. The drafts are kept here, in step 2's state, and copied into a
 * candidate's form values once their panel has loaded; from then on the form
 * holds the advisor's edits.
 */

export const tailoredHighlightSchema = z.object({
  highlight: z.string(),
  is_selected: z.boolean(),
  // The model's original wording, sent with the advisor's text so edits can
  // be measured; never shown to the search team.
  generated: z.string().nullish(),
  // Page-only, never sent: the resume roles the draft drew on.
  basis: z.string().nullish(),
});

export type TailoredHighlight = z.infer<typeof tailoredHighlightSchema>;

type CandidateValues = ParentSchema['candidates'][string];

/** A typed form path for one candidate's value. */
export const candidateField = <K extends keyof CandidateValues & string>(
  candidateUuid: string,
  name: K,
) => `candidates.${candidateUuid}.${name}` as const;

const POLL_MS = 1500;

const omitKey = <T,>(record: Record<string, T>, key: string) =>
  Object.fromEntries(Object.entries(record).filter(([k]) => k !== key));

type DraftState =
  | { status: 'pending' }
  | { status: 'failed'; error: string }
  | { status: 'ready'; taskId: string; highlights: TailoredHighlightDraft[] };

type ContextValue = {
  drafts: Record<string, DraftState | undefined>;
  regenerate: (candidateUuid: string) => void;
};

const TailoredHighlightsContext = createContext<ContextValue>({
  drafts: {},
  regenerate: () => undefined,
});

export const TailoredHighlightsProvider = TailoredHighlightsContext.Provider;

export const useTailoredHighlights = () =>
  useContext(TailoredHighlightsContext);

/**
 * Drafts highlights for each candidate that doesn't already have saved AI
 * highlights on its draft recommendation. Used once, by step 2.
 */
export function useTailoredHighlightDrafts({
  teamUuid,
  pipelineUuid,
  candidateUuids,
  skipUuids,
}: {
  teamUuid: string;
  pipelineUuid: string;
  candidateUuids: string[];
  skipUuids: Set<string>;
}): ContextValue {
  const [taskIds, setTaskIds] = useState<Record<string, string>>({});
  const [postErrors, setPostErrors] = useState<Record<string, string>>({});
  // Posted but not yet given a task id: shown as pending too.
  const [starting, setStarting] = useState<Record<string, true>>({});
  // Candidates already queued, so a re-render (or strict mode) never posts twice.
  const requested = useRef(new Set<string>());

  const { mutateAsync } = useMutation(postTailoredHighlightsMutation());

  const request = useCallback(
    (candidateUuid: string) => {
      requested.current.add(candidateUuid);
      setPostErrors((current) => omitKey(current, candidateUuid));
      setTaskIds((current) => omitKey(current, candidateUuid));
      setStarting((current) => ({ ...current, [candidateUuid]: true }));
      mutateAsync({
        path: { team_uuid: teamUuid, pipeline_uuid: pipelineUuid },
        body: { candidate_uuid: candidateUuid },
      })
        .then((queued) =>
          setTaskIds((current) => ({
            ...current,
            [candidateUuid]: queued.task_id,
          })),
        )
        .catch(() =>
          setPostErrors((current) => ({
            ...current,
            [candidateUuid]: 'Could not start drafting highlights.',
          })),
        )
        .finally(() =>
          setStarting((current) => omitKey(current, candidateUuid)),
        );
    },
    [mutateAsync, pipelineUuid, teamUuid],
  );

  useEffect(() => {
    candidateUuids
      .filter((uuid) => !skipUuids.has(uuid) && !requested.current.has(uuid))
      .forEach((uuid) => request(uuid));
  }, [candidateUuids, skipUuids, request]);

  const taskEntries = Object.entries(taskIds);
  const statuses = useQueries({
    queries: taskEntries.map(([, taskId]) => ({
      ...getTailoredHighlightsStatusOptions({
        path: {
          team_uuid: teamUuid,
          pipeline_uuid: pipelineUuid,
          task_id: taskId,
        },
      }),
      staleTime: Infinity,
      refetchInterval: (query: { state: { data?: { state: string } } }) =>
        !query.state.data || query.state.data.state === 'PENDING'
          ? POLL_MS
          : false,
    })),
  });

  // `statuses` is a new array each render; key the memo off its contents.
  const statusKey = JSON.stringify(statuses.map((s) => s.data));
  const drafts = useMemo(() => {
    const result: Record<string, DraftState> = {};
    Object.keys(starting).forEach((uuid) => {
      result[uuid] = { status: 'pending' };
    });
    Object.entries(postErrors).forEach(([uuid, error]) => {
      result[uuid] = { status: 'failed', error };
    });
    taskEntries.forEach(([uuid, taskId], index) => {
      const query = statuses[index];
      const data = query?.data;
      if (query?.isError || data?.state === 'FAILURE') {
        result[uuid] = {
          status: 'failed',
          error: data?.error ?? 'Drafting highlights failed. Try again.',
        };
      } else if (data?.state === 'SUCCESS') {
        result[uuid] = {
          status: 'ready',
          taskId,
          highlights: data.highlights ?? [],
        };
      } else {
        result[uuid] = { status: 'pending' };
      }
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starting, postErrors, taskIds, statusKey]);

  return { drafts, regenerate: request };
}

/** The "Written for this role" section of a candidate's highlight picker. */
export function TailoredHighlights({
  candidateUuid,
}: {
  candidateUuid: string;
}) {
  const { drafts, regenerate } = useTailoredHighlights();
  const draft = drafts[candidateUuid];
  const { setValue, watch } = useFormContext<ParentSchema>();
  const highlights = watch(`candidates.${candidateUuid}.tailored_highlights`);
  const appliedTaskId = watch(`candidates.${candidateUuid}.tailored_task_id`);

  // Copy finished drafts into the form once per
  // generation run, so switching between candidates never overwrites the
  // advisor's edits.
  useEffect(() => {
    if (draft?.status !== 'ready' || draft.taskId === appliedTaskId) {
      return;
    }
    setValue(
      candidateField(candidateUuid, 'tailored_highlights'),
      draft.highlights.map((h) => ({
        highlight: h.text,
        is_selected: true,
        generated: h.text,
        basis: h.basis,
      })),
      { shouldValidate: true },
    );
    setValue(candidateField(candidateUuid, 'tailored_task_id'), draft.taskId);
  }, [appliedTaskId, candidateUuid, draft, setValue]);

  const isPending = draft?.status === 'pending';

  return (
    <div className="flex flex-col gap-y-3 rounded-[8px] border border-purple-200 bg-purple-50/40 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-2 text-purple-800 [&>svg]:h-4 [&>svg]:w-4">
          <IconSparkles />
          <span className="text-14/100% font-medium">
            Written for this role
          </span>
        </div>
        <Button
          iconStart={<IconRefreshCw />}
          onClick={() => regenerate(candidateUuid)}
          size={26}
          variant="default-quaternary"
          disabled={isPending}
        >
          {highlights?.length ? 'Regenerate' : 'Draft'}
        </Button>
      </div>

      {isPending && (
        <div className="flex items-center gap-x-2 text-14 text-gray-600 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:animate-spin">
          <IconLoader />
          <span>
            {highlights?.length
              ? 'Redrafting highlights…'
              : 'Drafting highlights for this role…'}
          </span>
        </div>
      )}

      {draft?.status === 'failed' && (
        <div className="flex items-center gap-x-2 text-14 text-yellow-800 [&>svg]:h-4 [&>svg]:w-4">
          <IconAlertTriangle />
          <span>{draft.error}</span>
        </div>
      )}

      {Boolean(highlights?.length) && (
        <ul className="flex flex-col gap-y-3">
          {highlights?.map((highlight, index) => (
            <li
              key={`${appliedTaskId ?? 'saved'}-${index}`}
              className="flex gap-x-4"
            >
              <FormCheckbox
                className="flex flex-col pt-2"
                name={`candidates.${candidateUuid}.tailored_highlights.${index}.is_selected`}
              />
              <div className="flex flex-1 flex-col gap-y-1">
                <FormTextArea
                  className="bg-white"
                  maxLength={600}
                  // Tall enough to show a whole draft (up to 35 words) without
                  // scrolling; FormTextArea's default fits about three lines.
                  style={{ height: 140, maxHeight: 140, minHeight: 140 }}
                  name={`candidates.${candidateUuid}.tailored_highlights.${index}.highlight`}
                />
                {highlight.basis && (
                  <span className="text-12 font-normal text-gray-500">
                    From: {highlight.basis}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {draft?.status === 'ready' && !highlights?.length && (
        <span className="text-14 font-normal text-gray-500">
          The resume didn&apos;t support any highlights for this role.
        </span>
      )}
    </div>
  );
}
